import React from 'react';
import PropTypes from 'prop-types';
import ProgressBar from 'react-bootstrap/ProgressBar';
import {
  filterField,
  filterTier
} from 'containers/ProfileDetailsPage/profileUtils';
import LoadingIndicator from 'components/LoadingIndicator';
import ProfileIcon from 'components/ProfileIcon';
import { formatNumber } from 'utils/formatting';
import './ProfileCMPCard.css';

const ProfileCMPCard = ({ user, cmpStatus }) => {
  const isLoading = cmpStatus?.loading;
  const isError = cmpStatus?.error;

  if (isLoading) {
    return <LoadingIndicator />;
  } else if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to retrieve PLS Profile. If this continues, please contact
        support.
      </div>
    );
  }

  const { cmpPlayerStatus } = cmpStatus.statusV2;
  const { patronInfo } = cmpPlayerStatus;

  const firstName = filterField(patronInfo.firstName);
  const lastName = filterField(patronInfo.lastName);
  const profileName = firstName
    ? firstName.split(' ')[0].toLowerCase() + ' ' + lastName.toLowerCase()
    : user && user.displayName;

  const patronAccountNumber = cmpPlayerStatus.patronAccountNumber;
  const points = cmpPlayerStatus.basePointsEarned;
  const nextRewardTier = filterTier(cmpPlayerStatus.nextClubState);
  const rewardTier = filterTier(cmpPlayerStatus.clubState);
  const pointsToNextTier = cmpPlayerStatus.pointsNeeded;
  const rewardTierMax = pointsToNextTier + points;
  let tierClass = rewardTier ? 'tier-' + rewardTier.toLowerCase() : ' default';

  const renderTopSection = () => {
    const rewardsAccountInfo = patronAccountNumber
      ? `Rewards #${patronAccountNumber}`
      : 'Rewards Account Not Registered';

    return (
      <div className="card-top-section">
        <div className="tier-title">{rewardTier}</div>
        <ProfileIcon
          avatarData={user && user.avatarData}
          size={120}
          border="border"
        />
        <div className="user-info-container">
          <div className="user-name">{profileName}</div>
          <div>{rewardsAccountInfo}</div>
        </div>
      </div>
    );
  };

  const renderBottomSection = () => {
    let progressInfo;
    let progressPercentage;

    if (!nextRewardTier) {
      if (!rewardTier) {
        // no data
        progressInfo = (
          <div>
            Welcome to Wind Creek Casino! Your account is still being processed.
            Please check back soon!
          </div>
        );
      } else {
        // top tier
        progressInfo = (
          <div className="final-tier">
            You are a{' '}
            <span className="reward-card-name">
              <span>{rewardTier}</span>
            </span>{' '}
            card member, our premier tier with exclusive benefits!
          </div>
        );
      }
    } else {
      // not top tier
      progressInfo = (
        <div>
          <div>
            Earn <strong>{formatNumber(pointsToNextTier)} points</strong> to
            upgrade to:
          </div>
          <div className="reward-card-name">
            <span>{nextRewardTier}</span> card
          </div>
        </div>
      );
      // don't use rewardTierMin because only points reset, not level.
      // but minimum for next level is still the same.
      progressPercentage = (100.0 * points) / rewardTierMax;
    }

    return (
      <div className="card-bottom-section">
        <div className="progress-row">
          <h4 className="progress-ytd-">YTD Tier Points</h4>
          {progressPercentage !== undefined && (
            <div className="progress-bar-points">
              <ProgressBar now={progressPercentage} />
              <div className="points-counter">{`${points} / ${rewardTierMax}`}</div>
            </div>
          )}
          {progressInfo}
        </div>
      </div>
    );
  };

  return (
    <div className={`cmp-profile-card ${tierClass}`}>
      <h2 className="sr-only">Rewards Account Points</h2>
      <div className={'new-profile-tier'}>
        {renderTopSection()}
        {renderBottomSection()}
      </div>
    </div>
  );
};

ProfileCMPCard.propTypes = {
  user: PropTypes.shape({
    avatarData: PropTypes.string,
    displayName: PropTypes.string
  })
  // ADD CMP Data shape
};

export default ProfileCMPCard;
